import type { ColumnDef } from '@tanstack/react-table';
import {
  Hash,
  Package,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Percent,
  Store,
  Building2,
  AlertTriangle,
  Activity,
  Truck,
  Tag,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  Sparkles
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type { IItemCheckerReport } from '../../../api/ItemChecker.interface';

import LastIncomeColumn from './InComeCell';
import { ItemNameLinkCell } from './ItemNameLinkCell';
import { StockStatusCell } from './StatusColumn';

// Common cell wrapper with border
const CellWrapper = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={cn(
    "flex items-center justify-center h-full",
    "border-r border-border/50 dark:border-slate-700/50",
    className
  )}>
    {children}
  </div>
);

export const columns: ColumnDef<IItemCheckerReport>[] = [
  // Enhanced ID Column
  {
    accessorKey: 'ID',
    header: () => (
      <div className="flex items-center justify-center gap-1 border-r border-border/50 dark:border-slate-700/50">
        <Hash className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold">#</span>
      </div>
    ),
    size: 60,
    cell: ({ row }) => (
      <CellWrapper>
        <Badge
          variant="outline"
          className={cn(
            "h-7 w-12 justify-center font-mono text-xs font-medium",
            "bg-gradient-to-br from-muted/50 to-muted/30",
            "border-border/50 dark:border-slate-700/50"
          )}
        >
          {row.index + 1}
        </Badge>
      </CellWrapper>
    ),
  },

  {
    accessorKey: 'Name',
    header: () => (
      <div className="flex items-center gap-2 px-2 border-r border-border/50 dark:border-slate-700/50">
        <Package className="h-4 w-4 text-primary" />
        <span className="font-semibold">الصنف</span>
      </div>
    ),
    size: 250,
    cell: ({ row }) => (
      <div className="group px-2 border-r border-border/50 dark:border-slate-700/50">
        <ItemNameLinkCell
          className={cn(
            "relative flex items-center gap-3 rounded-lg",
            "bg-gradient-to-r from-background to-muted/10",
            "border border-border/50 px-3 py-2",
            "transition-all duration-200",
            "hover:border-primary/50 hover:bg-gradient-to-r hover:from-primary/5 hover:to-primary/10",
            "hover:shadow-md dark:hover:shadow-primary/10"
          )}
          id={row.original.ID}
          name={row.original.Name}
        />
      </div>
    ),
  },

  {
    accessorKey: 'Price',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <span className="font-semibold">جمهور</span>
      </div>
    ),
    size: 120,
    cell: ({ row }) => {
      const price = row.original.Price;
      return (
        <CellWrapper>
          <div className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-2",
            "bg-gradient-to-r from-emerald-50 to-emerald-100/50",
            "dark:from-emerald-950/30 dark:to-emerald-900/20",
            "border border-emerald-200 dark:border-emerald-800",
            "shadow-sm"
          )}>
            <span className="font-mono text-sm font-bold text-emerald-700 dark:text-emerald-300">
              {price?.toFixed(2) || '0.00'}
            </span>
            <span className="text-xs text-emerald-600 dark:text-emerald-400">
              ج.م
            </span>
          </div>
        </CellWrapper>
      );
    },
  },

  {
    accessorKey: 'DiscountIN',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <Percent className="h-4 w-4 text-purple-600 dark:text-purple-400" />
        <span className="font-semibold">مرجح</span>
      </div>
    ),
    size: 110,
    cell: ({ row }) => {
      const discount = row.original.DiscountIN;
      return (
        <CellWrapper>
          <Badge className={cn(
            "font-mono text-xs font-bold",
            "bg-gradient-to-r from-purple-500 to-purple-600 text-white",
            "border-0 shadow-sm"
          )}>
            {discount?.toFixed(2) || '0'}%
          </Badge>
        </CellWrapper>
      );
    },
  },

  {
    accessorKey: 'Count_Other',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <Store className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <span className="font-semibold">رصيد ب</span>
      </div>
    ),
    size: 100,
    cell: ({ row }) => {
      const count = row.original.Count_Other || 0;
      const hasStock = count > 0;
      return (
        <CellWrapper>
          <div className={cn(
            "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5",
            hasStock
              ? "bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-200 dark:border-blue-800 dark:from-blue-950/30 dark:to-blue-900/20"
              : "bg-gradient-to-r from-gray-50 to-gray-100/50 border-gray-200 dark:border-gray-800 dark:from-gray-950/30 dark:to-gray-900/20",
            "border shadow-sm"
          )}>
            {hasStock && <Activity className="h-3 w-3 text-blue-600 dark:text-blue-400" />}
            <span className={cn(
              "font-mono text-sm font-bold",
              hasStock
                ? "text-blue-700 dark:text-blue-300"
                : "text-gray-500 dark:text-gray-400"
            )}>
              {count}
            </span>
          </div>
        </CellWrapper>
      );
    },
  },

  // === GROUPED RETAIL (ق) COLUMNS === //
  {
    accessorKey: 'Count_Store_1',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-l-2 border-l-indigo-500 border-r-border/50 dark:border-r-slate-700/50 bg-indigo-50/30 dark:bg-indigo-950/20">
        <Store className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        <span className="font-semibold">رصيد ق</span>
      </div>
    ),
    size: 100,
    cell: ({ row }) => {
      const count = row.original.Count_Store_1 || 0;
      const hasStock = count > 0;
      return (
        <div className="flex items-center justify-center h-full border-r border-l-2 border-l-indigo-500/30 border-r-border/50 dark:border-r-slate-700/50 bg-indigo-50/10 dark:bg-indigo-950/10">
          <div className={cn(
            "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5",
            hasStock
              ? "bg-gradient-to-r from-indigo-50 to-indigo-100/50 dark:from-indigo-950/30 dark:to-indigo-900/20"
              : "bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-950/30 dark:to-gray-900/20",
            "border-2 border-indigo-300/50 dark:border-indigo-700/50",
            "shadow-sm"
          )}>
            {hasStock && <Activity className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />}
            <span className={cn(
              "font-mono text-sm font-bold",
              hasStock
                ? "text-indigo-700 dark:text-indigo-300"
                : "text-gray-500 dark:text-gray-400"
            )}>
              {count}
            </span>
          </div>
        </div>
      );
    },
  },

  {
    accessorKey: 'netQSales',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50 bg-indigo-50/30 dark:bg-indigo-950/20">
        <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
        <span className="font-semibold">بيع ق</span>
      </div>
    ),
    size: 120,
    cell: ({ row }) => {
      const net = row.original.netQSales || 0;
      const sales = row.original.sales?.retail || 0;
      const returns = row.original.returns?.retail || 0;
      
      return (
        <div className="flex items-center justify-center h-full border-r border-border/50 dark:border-slate-700/50 bg-indigo-50/10 dark:bg-indigo-950/10">
          <div className={cn(
            "flex flex-col items-center rounded-lg px-2 py-1",
            "border-2 border-green-300/50 dark:border-green-700/50",
            "bg-gradient-to-r from-green-50 to-green-100/50",
            "dark:from-green-950/30 dark:to-green-900/20"
          )}>
            <div className="flex items-center gap-1">
              <ArrowUpRight className="h-3 w-3 text-green-600 dark:text-green-400" />
              <span className="font-mono text-xs font-bold text-green-700 dark:text-green-300">
                {net}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-green-600 dark:text-green-400">+{sales}</span>
              {returns > 0 && <span className="text-red-600 dark:text-red-400">-{returns}</span>}
            </div>
          </div>
        </div>
      );
    },
  },

  {
    accessorKey: 'retailBalance',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50 bg-indigo-50/30 dark:bg-indigo-950/20">
        <Activity className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
        <span className="font-semibold">فحص ق</span>
      </div>
    ),
    size: 110,
    cell: ({ row }) => {
      const balance = row.original.retailBalance || 0;
      const isPositive = balance > 0;
      
      return (
        <div className="flex items-center justify-center h-full border-r border-border/50 dark:border-slate-700/50 bg-indigo-50/10 dark:bg-indigo-950/10">
          <Badge className={cn(
            "font-mono text-xs font-bold gap-1",
            "border-2",
            isPositive
              ? "bg-gradient-to-r from-green-500 to-green-600 text-white border-green-400/50"
              : "bg-gradient-to-r from-red-500 to-red-600 text-white border-red-400/50",
            "shadow-sm"
          )}>
            {isPositive ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {balance}
          </Badge>
        </div>
      );
    },
  },

  {
    accessorKey: 'retailStatus',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50 bg-indigo-50/30 dark:bg-indigo-950/20">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="font-semibold">حالة ق</span>
      </div>
    ),
    size: 120,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
    cell: ({ row }) => (
      <div className="flex items-center justify-center h-full border-r border-border/50 dark:border-slate-700/50 bg-indigo-50/10 dark:bg-indigo-950/10">
        <StockStatusCell
          className="border-2 rounded-lg"
          status={row.original.retailStatus}
        />
      </div>
    ),
  },

  // === GROUPED WHOLESALE (ج) COLUMNS === //
  {
    accessorKey: 'Count_Store_2',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-l-2 border-l-violet-500 border-r-border/50 dark:border-r-slate-700/50 bg-violet-50/30 dark:bg-violet-950/20">
        <Store className="h-4 w-4 text-violet-600 dark:text-violet-400" />
        <span className="font-semibold">رصيد ج</span>
      </div>
    ),
    size: 100,
    cell: ({ row }) => {
      const count = row.original.Count_Store_2 || 0;
      const hasStock = count > 0;
      return (
        <div className="flex items-center justify-center h-full border-r border-l-2 border-l-violet-500/30 border-r-border/50 dark:border-r-slate-700/50 bg-violet-50/10 dark:bg-violet-950/10">
          <div className={cn(
            "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5",
            hasStock
              ? "bg-gradient-to-r from-violet-50 to-violet-100/50 dark:from-violet-950/30 dark:to-violet-900/20"
              : "bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-950/30 dark:to-gray-900/20",
            "border-2 border-violet-300/50 dark:border-violet-700/50",
            "shadow-sm"
          )}>
            {hasStock && <Activity className="h-3 w-3 text-violet-600 dark:text-violet-400" />}
            <span className={cn(
              "font-mono text-sm font-bold",
              hasStock
                ? "text-violet-700 dark:text-violet-300"
                : "text-gray-500 dark:text-gray-400"
            )}>
              {count}
            </span>
          </div>
        </div>
      );
    },
  },

  {
    accessorKey: 'netWSales',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50 bg-violet-50/30 dark:bg-violet-950/20">
        <TrendingUp className="h-4 w-4 text-teal-600 dark:text-teal-400" />
        <span className="font-semibold">بيع ج</span>
      </div>
    ),
    size: 120,
    cell: ({ row }) => {
      const net = row.original.netWSales || 0;
      const sales = row.original.sales?.wholesale || 0;
      const returns = row.original.returns?.wholesale || 0;
      
      return (
        <div className="flex items-center justify-center h-full border-r border-border/50 dark:border-slate-700/50 bg-violet-50/10 dark:bg-violet-950/10">
          <div className={cn(
            "flex flex-col items-center rounded-lg px-2 py-1",
            "border-2 border-teal-300/50 dark:border-teal-700/50",
            "bg-gradient-to-r from-teal-50 to-teal-100/50",
            "dark:from-teal-950/30 dark:to-teal-900/20"
          )}>
            <div className="flex items-center gap-1">
              <ArrowUpRight className="h-3 w-3 text-teal-600 dark:text-teal-400" />
              <span className="font-mono text-xs font-bold text-teal-700 dark:text-teal-300">
                {net}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-teal-600 dark:text-teal-400">+{sales}</span>
              {returns > 0 && <span className="text-red-600 dark:text-red-400">-{returns}</span>}
            </div>
          </div>
        </div>
      );
    },
  },
  
  {
    accessorKey: 'wholesaleBalance',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50 bg-violet-50/30 dark:bg-violet-950/20">
        <Activity className="h-4 w-4 text-rose-600 dark:text-rose-400" />
        <span className="font-semibold">فحص ج</span>
      </div>
    ),
    size: 110,
    cell: ({ row }) => {
      const balance = row.original.wholesaleBalance || 0;
      const isPositive = balance > 0;
      
      return (
        <div className="flex items-center justify-center h-full border-r border-border/50 dark:border-slate-700/50 bg-violet-50/10 dark:bg-violet-950/10">
          <Badge className={cn(
            "font-mono text-xs font-bold gap-1",
            "border-2",
            isPositive
              ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white border-teal-400/50"
              : "bg-gradient-to-r from-rose-500 to-rose-600 text-white border-rose-400/50",
            "shadow-sm"
          )}>
            {isPositive ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {balance}
          </Badge>
        </div>
      );
    },
  },

  {
    accessorKey: 'wholesaleStatus',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50 bg-violet-50/30 dark:bg-violet-950/20">
        <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
        <span className="font-semibold">حالة ج</span>
      </div>
    ),
    size: 120,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
    cell: ({ row }) => (
      <div className="flex items-center justify-center h-full border-r border-border/50 dark:border-slate-700/50 bg-violet-50/10 dark:bg-violet-950/10">
        <StockStatusCell
          className="border-2 rounded-lg"
          status={row.original.wholesaleStatus}
        />
      </div>
    ),
  },

  // === OTHER COLUMNS === //
  {
    accessorKey: 'lastIncomingDate',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <Truck className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        <span className="font-semibold">توريد</span>
      </div>
    ),
    size: 140,
    cell: ({ row }) => (
      <CellWrapper>
        <LastIncomeColumn
          LastIncomeDate={row.original.lastIncomingDate}
          Qty={row.original.lastIncoming?.LastQty || 0}
          discount={row.original.lastIncoming?.LastDiscount || 0}
          supplierName={row.original.lastIncoming?.SupplierName || ''}
        />
      </CellWrapper>
    ),
  },

  {
    accessorKey: 'offersCount',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <Tag className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        <span className="font-semibold">عروض</span>
      </div>
    ),
    size: 90,
    cell: ({ row }) => {
      const count = row.original.offersCount || 0;
      return (
        <CellWrapper>
          {count > 0 ? (
            <Badge className={cn(
              "font-mono text-xs font-bold",
              "bg-gradient-to-r from-gray-500 to-gray-600 text-white",
              "border-0 shadow-sm"
            )}>
              {count}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </CellWrapper>
      );
    },
  },

  {
    accessorKey: 'offersMaxDiscount',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
        <span className="font-semibold">أعلى</span>
      </div>
    ),
    size: 90,
    cell: ({ row }) => {
      const max = row.original.offersMaxDiscount || 0;
      return (
        <CellWrapper>
          {max > 0 ? (
            <Badge className={cn(
              "font-mono text-xs font-bold",
              "bg-gradient-to-r from-green-500 to-green-600 text-white",
              "border-0 shadow-sm"
            )}>
              {max}%
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </CellWrapper>
      );
    },
  },

  {
    accessorKey: 'offersMinDiscount',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
        <span className="font-semibold">أقل</span>
      </div>
    ),
    size: 90,
    cell: ({ row }) => {
      const min = row.original.offersMinDiscount || 0;
      return (
        <CellWrapper>
          {min > 0 ? (
            <Badge className={cn(
              "font-mono text-xs font-bold",
              "bg-gradient-to-r from-red-500 to-red-600 text-white",
              "border-0 shadow-sm"
            )}>
              {min}%
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </CellWrapper>
      );
    },
  },

  {
    accessorKey: 'Company_Name',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <span className="font-semibold">شركة</span>
      </div>
    ),
    size: 150,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
    cell: ({ row }) => (
      <CellWrapper className="px-2">
        <Badge
          variant="outline"
          className={cn(
            "px-2 py-1 text-xs font-medium",
            "bg-gradient-to-r from-blue-50 to-blue-100/50",
            "dark:from-blue-950/30 dark:to-blue-900/20",
            "border-blue-200 dark:border-blue-800",
            "text-blue-700 dark:text-blue-300"
          )}
        >
          {row.original.Company_Name || '-'}
        </Badge>
      </CellWrapper>
    ),
  },

  {
    accessorKey: 'Place_Name',
    header: () => (
      <div className="flex items-center justify-center gap-2 border-r border-border/50 dark:border-slate-700/50">
        <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="font-semibold">باكيه</span>
      </div>
    ),
    size: 150,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
    cell: ({ row }) => (
      <CellWrapper className="px-2">
        <Badge
          variant="outline"
          className={cn(
            "px-2 py-1 text-xs font-medium",
            "bg-gradient-to-r from-amber-50 to-amber-100/50",
            "dark:from-amber-950/30 dark:to-amber-900/20",
            "border-amber-200 dark:border-amber-800",
            "text-amber-700 dark:text-amber-300"
          )}
        >
          {row.original.Place_Name || '-'}
        </Badge>
      </CellWrapper>
    ),
  },
];